import React, { useState } from 'react'
import { InputAdornment, TextField, Grid } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { calendarIcon } from '../../../assets/fontawesome/calendarIcon'
import { makeStyles } from '@mui/styles'
import { getSettingsStyles } from '../../../styles/settingsStyles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  endAdornmentIcon: {
    height: 21,
    width: 21,
    color: theme.palette.generalUi.icons,
  },
  calendarField: {
    '& .MuiOutlinedInput-input': {
      fontSize: 14,
    },
    '& .MuiInputLabel-root': {
      fontSize: 14,
    },

    '& .MuiInputAdornment-positionEnd': {
      marginRight: 4,
    },
    '& .MuiIconButton-root': {
      padding: 6,
    },
  },
  dornmentFix: {
    marginRight: 4,
    '& .MuiIconButton-root': {
      padding: 6,
    },
  },
}))

const isValidDate = date => {
  return date && date.isValid && date.isValid()
}

const parseDate = date => {
  if (!date) return null
  const parsedDate = moment(date)
  return parsedDate.isValid() ? parsedDate : null
}

const DateRangePicker = ({
  initialDateRange,
  onDateRangeChange,
  trackDisableStatus,
  onErrorChange = () => {},
}) => {
  const classes = useStyles()
  const [dateRange, setDateRange] = useState({
    startDate: moment(initialDateRange.startDate),
    endDate: moment(initialDateRange.endDate),
  })
  const [error, setError] = useState({ startDate: null, endDate: null })

  const handleDateChange = (date, isStartDate) => {
    const parsedDate = parseDate(date)

    if (!parsedDate) {
      setError(prev => ({
        ...prev,
        [isStartDate ? 'startDate' : 'endDate']: 'Invalid Date Format',
      }))
      onErrorChange(true)
      return
    }

    // Always update the state first
    const updatedRange = {
      ...dateRange,
      [isStartDate ? 'startDate' : 'endDate']: parsedDate,
    }

    setDateRange(updatedRange)

    // Validate after update
    let startError = null
    let endError = null

    if (updatedRange.startDate.isAfter(updatedRange.endDate)) {
      if (isStartDate) {
        startError = 'Start date cannot be after end date'
      } else {
        endError = 'End date cannot be before start date'
      }
    }

    const hasError = !!(startError || endError)
    setError({ startDate: startError, endDate: endError })
    onErrorChange(hasError)

    // Always propagate latest range
    onDateRangeChange({
      startDate: updatedRange.startDate.toDate(),
      endDate: updatedRange.endDate.toDate(),
    })
  }

  return (
    <>
      <Grid item md={6}>
        <DatePicker
          disabled={trackDisableStatus}
          label="From"
          value={dateRange.startDate}
          onChange={date => handleDateChange(date, true)}
          inputFormat="YYYY-MM-DD"
          components={{
            OpenPickerIcon: () => (
              <FontAwesomeIcon
                icon={calendarIcon}
                className={classes.endAdornmentIcon}
              />
            ),
          }}
          PopperProps={{
            placement: 'bottom-end',
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              margin="dense"
              fullWidth
              error={!!error.startDate}
              helperText={error.startDate || ''}
              InputProps={{
                ...params.InputProps,
                className: classes.calendarField,
                endAdornment: params.InputProps.endAdornment,
              }}
            />
          )}
        />
      </Grid>
      <Grid item md={6}>
        <DatePicker
          disabled={trackDisableStatus}
          label="To"
          value={dateRange.endDate}
          onChange={date => handleDateChange(date, false)}
          inputFormat="YYYY-MM-DD"
          PopperProps={{
            placement: 'bottom-start',
          }}
          components={{
            OpenPickerIcon: () => (
              <FontAwesomeIcon
                icon={calendarIcon}
                className={classes.endAdornmentIcon}
              />
            ),
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              margin="dense"
              fullWidth
              error={!!error.endDate}
              helperText={error.endDate || ''}
              InputProps={{
                ...params.InputProps,
                className: classes.calendarField,
                endAdornment: params.InputProps.endAdornment,
              }}
            />
          )}
        />
      </Grid>
    </>
  )
}

export default DateRangePicker
